import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { api } from '../lib/api';
import { logger } from '../lib/logger';
import { useAuth } from '../lib/auth-context';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Shuffle, Sprout, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

// ─── Assignment mode definitions ───────────────────────────────────────
// Tre modalità di assegnazione disponibili quando si crea un lead manuale.
// Ogni modalità è chiaramente etichettata nel segmented control + ha una
// descrizione che spiega cosa succede operativamente. Match esatto col
// nuovo backend `assignment_mode` su POST /api/leads.
const ASSIGN_MODES = [
  {
    key: 'round_robin',
    title: 'Round-Robin',
    icon: Shuffle,
    color: 'bg-slate-100 border-slate-300 text-slate-700',
    activeColor: 'bg-slate-700 border-slate-700 text-white',
    get desc() { return i18n.t('newLead.modes.roundRobinDesc', "Comportamento standard: il lead entra in pipeline e verrà assegnato automaticamente al prossimo Associate disponibile."); },
  },
  {
    key: 'organic',
    get title() { return i18n.t('newLead.modes.organicTitle', 'Organico di'); },
    icon: Sprout,
    color: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    activeColor: 'bg-emerald-700 border-emerald-700 text-white',
    get desc() { return i18n.t('newLead.modes.organicDesc', "Il lead viene da un agente specifico ma NON l'ha ancora qualificato. Parte un'email magic-link al cliente per fargli vedere il VSL e fare Form 1."); },
  },
  {
    key: 'manual_assign',
    get title() { return i18n.t('newLead.modes.manualTitle', 'Assegnato a'); },
    icon: UserCheck,
    color: 'bg-amber-50 border-amber-200 text-amber-800',
    activeColor: 'bg-amber-600 border-amber-600 text-white',
    get desc() { return i18n.t('newLead.modes.manualDesc', "Assegna DIRETTAMENTE in pipeline a un agente (senza magic-link, senza email cliente). Usato quando inserisci un lead che stai già lavorando."); },
  },
];

export default function NewLeadPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user } = useAuth() || {};
  const [agents, setAgents] = useState([]);
  const [specialFlags, setSpecialFlags] = useState(null);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', source: 'manual', branch: 'pre_branch', notes: '',
    assignment_mode: 'round_robin',
    selected_agent_id: '',
    profile_type: '',  // 2026-06-22: special associates default to 'real_estate'
  });
  const [submitting, setSubmitting] = useState(false);

  // Load active agents for the assignment dropdown + special flags
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [agentsRes, flagsRes] = await Promise.all([
          api.get('/agents'),
          api.get('/leads/_my-special-flags').catch(() => ({ data: null })),
        ]);
        const list = Array.isArray(agentsRes.data) ? agentsRes.data : (agentsRes.data?.items || []);
        const active = list.filter((a) => a.is_active !== false);
        if (cancelled) return;
        setAgents(active);
        const flags = flagsRes?.data || null;
        setSpecialFlags(flags);

        // Default agent: se l'utente corrente è un agente, pre-seleziona se stesso
        // 2026-06-25: prima proviamo match su agent_id (più affidabile), poi
        // fallback su email (case-insensitive). Così l'auto-assegnazione del
        // creator funziona sia per organic che per manual_assign.
        let preselectedAgentId = '';
        if (user?.agent_id) {
          const me = active.find((a) => a.id === user.agent_id);
          if (me) preselectedAgentId = me.id;
        }
        if (!preselectedAgentId && user?.email) {
          const me = active.find((a) => (a.email || '').toLowerCase() === user.email.toLowerCase());
          if (me) preselectedAgentId = me.id;
        }

        // ★ 2026-06-22 (UPDATED 2026-06-27 — bugfix richiesta utente):
        //   Per gli Associate speciali Albania (Pacelli/Pamela/Padoan) NON forziamo
        //   più assignment_mode='organic' di default. Possono scegliere tra le 3
        //   modalità come tutti gli altri. Il default è 'manual_assign' con
        //   loro stessi pre-selezionati (auto-assegnazione veloce).
        //   Pre-impostiamo solo branch=albania + profile_type=real_estate
        //   (modificabili). Il backend gestisce poi la restrizione del Senior
        //   dropdown (Mattia/Maietta/Padoan).
        if (flags?.is_special_organic_associate) {
          setForm((f) => ({
            ...f,
            assignment_mode: 'manual_assign',  // default: assegna a se stesso
            branch: 'albania',
            profile_type: flags.default_profile_type || 'real_estate',
            selected_agent_id: preselectedAgentId,
          }));
        } else if (preselectedAgentId) {
          setForm((f) => ({ ...f, selected_agent_id: preselectedAgentId }));
        }
      } catch (e) {
        logger.debug('NewLeadPage: agents/flags load failed', e);
      }
    })();
    return () => { cancelled = true; };
    // ★ 2026-07-29 (hooks fix): re-run only when the logged-in user's email
    // or agent_id changes (login/logout/link). Intentionally not depending on
    // the full `user` object to avoid re-fetching on every render.
     
  }, [user?.email, user?.agent_id]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error(t('newLead.nameRequired', 'Nome obbligatorio')); return; }
    if (form.assignment_mode !== 'round_robin' && !form.selected_agent_id) {
      toast.error(t('newLead.agentRequired', 'Seleziona un agente per questa modalità'));
      return;
    }
    if (form.assignment_mode === 'organic' && !form.email.trim()) {
      toast.error(t('newLead.emailRequired', 'Per i lead "Organico" serve email (per inviare il magic-link)'));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        source: form.source,
        branch: form.branch,
        notes: form.notes,
        custom_fields: {
          // The backend reads `_assignment_mode` and `_manual_assignee_id`
          // (or `organic_owner_id` for organic mode) from custom_fields.
          _assignment_mode: form.assignment_mode,
        },
      };
      if (form.assignment_mode === 'organic') {
        payload.custom_fields.organic_owner_id = form.selected_agent_id;
      } else if (form.assignment_mode === 'manual_assign') {
        payload.custom_fields._manual_assignee_id = form.selected_agent_id;
      }
      // ★ 2026-06-22: Special associates pre-fill profile_type on the lead so
      //   the Albania-only / Real Estate context is preserved through the pipeline.
      if (form.profile_type) {
        payload.custom_fields.profile_type = form.profile_type;
      }
      // ★ 2026-06-22 (UPDATED 2026-06-27): mark lead as created by a Special
      //   Albania associate so we can keep statistics / filtering. NON è più
      //   collegato a "organic" — l'utente può scegliere qualsiasi modalità.
      if (specialFlags?.is_special_organic_associate) {
        payload.custom_fields.is_albania_special_creator = true;
      }
      const r = await api.post('/leads', payload);
      toast.success(t('newLead.created', 'Lead creato con successo'));
      nav(`/app/leads/${r.data.id}`);
    } catch (e) {
      // Pydantic v2 può restituire `detail` come lista di oggetti
      // (validation errors). Normalizziamolo in stringa leggibile per il toast.
      const detail = e?.response?.data?.detail;
      let msg = t('newLead.createError', 'Errore creazione lead');
      if (typeof detail === 'string') {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        msg = detail
          .map((d) => {
            const field = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : '';
            return `${field ? field + ': ' : ''}${d.msg || ''}`;
          })
          .join(' · ');
      } else if (detail && typeof detail === 'object') {
        msg = JSON.stringify(detail);
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const currentMode = ASSIGN_MODES.find((m) => m.key === form.assignment_mode);
  const needsAgent = form.assignment_mode !== 'round_robin';

  return (
    <div className="p-4 md:p-6">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="newlead-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> {t('common.back', 'Indietro')}
      </Button>
      <Card className="max-w-3xl mt-3 border-border" data-testid="new-lead-card">
        <CardContent className="p-6">
          <h1 className="font-serif text-2xl text-[hsl(var(--primary))] mb-1">{t('newLead.title', 'Nuovo lead')}</h1>
          <p className="text-xs text-muted-foreground mb-5">
            {t('newLead.subtitle', 'Compila i dati e scegli come il lead deve entrare nel sistema.')}
          </p>

          {/* ★ 2026-06-22 — Special Albania associate banner (UPDATED 2026-06-27) */}
          {specialFlags?.is_special_organic_associate && (
            <div
              className="mb-5 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900"
              data-testid="special-organic-banner"
            >
              <div className="font-semibold mb-1">🇦🇱 Associate speciale Albania</div>
              <div>
                Branch e profilo sono pre-impostati su <b>Albania · Real Estate</b> (modificabili).
                Puoi scegliere la modalità di assegnazione (round-robin, organico, manuale):
                il default è <b>manuale a te stesso</b>. Per gli appuntamenti potrai
                scegliere il Senior solo tra <b>Mattia Demichele e
                Alessandro Padoan</b>.
              </div>
            </div>
          )}

          {/* ── Anagrafica ──────────────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t('newLead.name', 'Nome')}*</Label>
              <Input data-testid="newlead-name" value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" data-testid="newlead-email" value={form.email}
                     onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('newLead.phone', 'Telefono')}</Label>
              <Input data-testid="newlead-phone" value={form.phone}
                     onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t('newLead.source', 'Sorgente')}</Label>
              <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                <SelectTrigger data-testid="newlead-source"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">{t('newLead.manualEntry', 'Inserimento manuale')}</SelectItem>
                  <SelectItem value="meta">Meta Ads</SelectItem>
                  <SelectItem value="tiktok">TikTok Ads</SelectItem>
                  <SelectItem value="google">Google Ads</SelectItem>
                  <SelectItem value="api">API</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('newLead.initialBranch', 'Branch iniziale')}</Label>
              <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                <SelectTrigger data-testid="newlead-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pre_branch">Pre-branch</SelectItem>
                  <SelectItem value="equity_estate">Equity Estate</SelectItem>
                  <SelectItem value="real_estate">Real Estate</SelectItem>
                  <SelectItem value="albania">🇦🇱 Albania (Real Estate)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* ── Assignment Mode segmented control ───────────────────── */}
          <div className="mt-6 mb-2">
            <Label className="text-sm font-semibold text-[hsl(var(--primary))]">
              {t('newLead.assignMode', 'Modalità di assegnazione')}
            </Label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              {t('newLead.assignModeHint', 'Scegli come questo lead deve entrare nel sistema.')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3" data-testid="newlead-assign-modes">
            {ASSIGN_MODES.map((m) => {
              const Icon = m.icon;
              const isActive = form.assignment_mode === m.key;
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => setForm({ ...form, assignment_mode: m.key })}
                  data-testid={`newlead-mode-${m.key}`}
                  className={`text-left px-4 py-3 rounded-lg border-2 transition-all ${
                    isActive ? m.activeColor : m.color + ' hover:border-current'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" />
                    <span className="font-semibold text-sm">{m.title}</span>
                  </div>
                  <p className={`text-[10.5px] leading-snug ${isActive ? 'text-white/90' : 'opacity-80'}`}>
                    {m.desc}
                  </p>
                </button>
              );
            })}
          </div>

          {/* ── Agent dropdown (visible only when mode != round_robin) ──── */}
          {needsAgent && (
            <div className="space-y-1.5 mb-3" data-testid="newlead-agent-section">
              <Label className="flex items-center gap-1.5">
                {currentMode?.icon ? React.createElement(currentMode.icon, { className: 'w-3.5 h-3.5' }) : null}
                {form.assignment_mode === 'organic'
                  ? t('newLead.organicOwner', 'Agente proprietario (organico)')
                  : t('newLead.pipelineTarget', 'Agente destinatario (in pipeline)')}
                <span className="text-red-600">*</span>
              </Label>
              <Select
                value={form.selected_agent_id || ''}
                onValueChange={(v) => setForm({ ...form, selected_agent_id: v })}
              >
                <SelectTrigger data-testid="newlead-agent-select">
                  <SelectValue placeholder={t('newLead.selectAgent', '— Seleziona agente —')} />
                </SelectTrigger>
                <SelectContent>
                  {agents.length === 0 && (
                    <SelectItem value="loading" disabled>{t('newLead.loadingAgents', 'Caricamento agenti…')}</SelectItem>
                  )}
                  {agents.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}{a.role_label ? ` · ${a.role_label}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.assignment_mode === 'organic' && (
                <p className="text-[11px] text-emerald-700 mt-1 leading-relaxed">
                  💚 {t('newLead.organicNote', 'Verrà mandata un\'email al cliente con magic-link al VSL. L\'agente resterà marchiato come proprietario organico. Dopo il Form 1, parte il routing standard ma il credit resta a')} {agents.find((a) => a.id === form.selected_agent_id)?.name || '—'}.
                </p>
              )}
              {form.assignment_mode === 'manual_assign' && (
                <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
                  ⚡ {t('newLead.manualNotePre', 'Il lead va direttamente in pipeline con')}{' '}
                  <b>{agents.find((a) => a.id === form.selected_agent_id)?.name || '—'}</b>{' '}
                  {t('newLead.manualNotePost', 'come Associate/Senior. NIENTE magic-link, NIENTE email al cliente — pronto per essere chiamato/lavorato subito.')}
                </p>
              )}
            </div>
          )}

          <div className="space-y-1.5 mt-3">
            <Label>{t('newLead.notes', 'Note')}</Label>
            <Textarea
              rows={4}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              data-testid="newlead-notes"
              placeholder={t('newLead.notesPlaceholder', 'Note interne (es. contesto, da dove arriva, particolarità)…')}
            />
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => nav(-1)} data-testid="newlead-cancel">
              {t('common.cancel', 'Annulla')}
            </Button>
            <Button onClick={submit} disabled={submitting} data-testid="newlead-submit">
              {submitting ? t('common.saving', 'Salvataggio…') : t('newLead.create', 'Crea lead')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
